import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { MetricCodesService } from '@app/core/services/metric-codes.service';
import { MetricMappingsService } from '@app/modules/companies/services/metric-mappings.service';
import { TearsheetService } from '@app/modules/companies/services/tearsheet.service';
import { TearsheetPackageService } from '@app/modules/portfolio/services/tearsheet-package/tearsheet-package.service';
import { IPortfolioCompany } from '@common/models/IPortfolioCompany';
import { IMetricProps, ITearSheet, TearsheetSections } from '@common/models/ITearSheet';
import { getLastDateInQuarter, getQuarter } from '@common/services/DateHelper';
import TearsheetHelperService from '@common/services/TearsheetHelperService';
import { ConfirmationService } from 'primeng/api';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';

@Component({
  selector: 'app-tearsheet-edit-dialog',
  templateUrl: './tearsheet-edit-dialog.component.html',
  styleUrls: ['./tearsheet-edit-dialog.component.scss'],

})
export class TearsheetEditDialogComponent implements OnChanges, OnDestroy {
  @Input() tearSheet: ITearSheet;
  @Input() hiddenMetrics: any;
  @Input() company: IPortfolioCompany;
  helper: TearsheetHelperService;
  @Output() save = new EventEmitter<ITearSheet>();
  @Input() fundSummary ;
  @Input() activeFundIndex;
  @Input() quarterDateArray;
  editTearsheetData: ITearSheet;
  isEditMode:  boolean = true;
  isKeyStatsEnabled: boolean = false;
  @Output() cancelled = new EventEmitter<void>();
  selectedQuarterDate;
  private subscription: Subscription = new Subscription();

  constructor(private readonly confirmationService: ConfirmationService, private readonly tearsheetService: TearsheetService,
    private readonly metricCodesService: MetricCodesService, private readonly tearsheetPackageService: TearsheetPackageService,
     private readonly metricMappingsService: MetricMappingsService
  ) { }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnChanges(): void {
    this.editTearsheetData = structuredClone(this.tearSheet);
    if(this.editTearsheetData?.keyOperatingStats){
        for(let stats in this.editTearsheetData.keyOperatingStats){
            this.editTearsheetData.keyOperatingStats[stats]['isEdit'] = true;
        }
    }
    if (this.company) {
      this.helper = new TearsheetHelperService(this.company, this.editTearsheetData, []);
    }
    this.selectedQuarterDate = this.getQuarterName(this.editTearsheetData?.generalInfo?.operatingMetricsDate) ;
  }
  getQuarterName(date){
    return getQuarter(new Date(date))
  }
  areEditingMetricsEqual(current: any, edited: any): boolean {
  // Compare investmentProfile objects
  const investmentProfileEqual = this.deepEqual(
    current.investmentProfile,
    edited.investmentProfile
  );

  // Compare the fundInvestmentSummary items at activeFundIndex
  const currentFundSummary = current.fundInvestmentSummary?.[this.activeFundIndex];
  const editedFundSummary = edited.fundInvestmentSummary?.[this.activeFundIndex];
  const fundSummaryEqual = this.deepEqual(currentFundSummary, editedFundSummary);
  return investmentProfileEqual && fundSummaryEqual;
}

// Helper method for deep equality check (only for the relevant fields)
deepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;
  if (
    typeof obj1 !== 'object' ||
    obj1 === null ||
    typeof obj2 !== 'object' ||
    obj2 === null
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    const val1 = obj1[key];
    const val2 = obj2[key];

    if (typeof val1 === 'object' && typeof val2 === 'object') {
      if (!this.deepEqual(val1, val2)) return false;
    } else if (val1 !== val2) { return false;
    }
  }
  return true;
}

  onCancel(): void {
    const hasChanges = !this.areEditingMetricsEqual(this.tearSheet, this.editTearsheetData);
    if(hasChanges)
    {
      this.confirmationService.confirm({
          message: 'You have unsaved changes. Are you sure you want to discard them?',
          header: 'Unsaved Changes',
          icon: 'pi pi-exclamation-triangle',
          accept: () => {
            this.editTearsheetData = structuredClone(this.tearSheet); // Restore
            this.cancelled.emit();
          },
          reject: () => {
            // Do nothing,
          },
      })
    }
    else {
      // No changes detected, reset companies directly
      this.editTearsheetData = structuredClone(this.tearSheet);
      this.cancelled.emit();
    }
  }
  public onMetricValueUpdate(editedMetricProp: IMetricProps, tearsheetPropertyName: string, metricPropertyName: string): void {
    metricPropertyName = metricPropertyName.trim();
    editedMetricProp.metricCode = metricPropertyName;
      // Update value, metricCode, AND displayName in the parent object
      let metric ;
      if(tearsheetPropertyName === 'fundInvestmentSummary'){
        metric = this.editTearsheetData[tearsheetPropertyName][this.activeFundIndex][metricPropertyName];
      }
      else{
        metric = this.editTearsheetData[tearsheetPropertyName][metricPropertyName];
      }
      if (metric) {
          metric.value = editedMetricProp.value;
          metric.metricCode = metricPropertyName;

          // Update displayName if it exists in the edited metric prop
          if ('displayName' in editedMetricProp && editedMetricProp.displayName !== undefined) {
              metric.displayName = editedMetricProp.displayName;
          }
      }
  }
  onKeyStatsChange(event){
    this.isKeyStatsEnabled = event
  }
  onTearsheetDownload(){
        const fund : any = this.editTearsheetData.fundInvestmentSummary[this.activeFundIndex].fund.value;
        // Set investmentSummary value
        this.editTearsheetData.investmentSummary = this.editTearsheetData.investmentSummaryWithExcludePartner;
        this.editTearsheetData.noOperatingMetrics = this.isKeyStatsEnabled;
        this.subscription.add(this.tearsheetService.downloadLiveTearsheet(this.editTearsheetData.mnemonic, fund, this.editTearsheetData)
        .subscribe((res: ArrayBuffer) => {
          this.downloadTearsheet(res);
          this.editTearsheetData = structuredClone(this.tearSheet);
          this.cancelled.emit();
        }));
  }
  onTearsheetPackageDownload(){
      const fund : any = this.editTearsheetData.fundInvestmentSummary[this.activeFundIndex].fund.value;
      this.editTearsheetData.noOperatingMetrics = this.isKeyStatsEnabled;
      this.subscription.add(this.tearsheetPackageService.downloadLiveTearsheetPackage(this.editTearsheetData.mnemonic, fund, this.editTearsheetData?.name, this.editTearsheetData)
      .subscribe((res: ArrayBuffer) => {
        this.downloadTearsheet(res, fund, 'package');
        this.editTearsheetData = structuredClone(this.tearSheet);
        this.cancelled.emit();
      }));
  }
  private downloadTearsheet(data: ArrayBuffer, fund = '', type= 'tearsheet'): void {
        let file = new Blob([data], { type: 'application/pdf' });
        const packageFileName = `${fund}_${this.editTearsheetData?.name}_tearsheet_package.pdf`;
        const fileName = type === 'package' ? packageFileName : this.helper.outputFileName(this.editTearsheetData.fundInvestmentSummary[this.activeFundIndex].fund.value as string);
        const fileURL = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = fileURL;
        link.target = '_blank';
        link.download = fileName;
        link.click();

        // Cleanup
        setTimeout(() => {
            URL.revokeObjectURL(fileURL);
        }, 100);
  }
  async onQuarterDateSelection(selectedQuarter: any) {
      this.selectedQuarterDate = selectedQuarter;
      const d = getLastDateInQuarter(selectedQuarter);
      const quarter = getQuarter(d);

      try {
          const metrics = await firstValueFrom(this.tearsheetService.getMetrics(this.editTearsheetData.mnemonic, quarter));
          this.helper.setMetrics(metrics);
          // Fetch section metrics in parallel
          const { keyOps, profile } = await firstValueFrom(
              forkJoin({
                  keyOps: this.metricMappingsService.getSectionMetrics(TearsheetSections.keyOperatingStats, this.company),
                  profile: this.metricMappingsService.getSectionMetrics(TearsheetSections.investmentProfile, this.company),
              })
          );

          // Now  load key stats and profile (await the Promises)
          const keystats = await this.helper.loadKeyOperatingStats(keyOps);
          this.editTearsheetData.generalInfo.operatingMetricsDate = d;
          const profileStats = await this.helper.loadInvestmentProfile(true, profile);

          this.editTearsheetData.investmentProfile = profileStats;
          this.refreshTearsheetValues(keystats);

      } catch (err) {}
    }
   public onEditClick(iseditable: boolean): void {
        this.isEditMode = iseditable;
        if( this.editTearsheetData.investmentProfile?.ownership){
             this.editTearsheetData.investmentProfile.ownership.isEdit = iseditable;
        }

        if(this.editTearsheetData.investmentProfile?.TS_IP_NCI){
            this.editTearsheetData.investmentProfile.TS_IP_NCI.isEdit = iseditable;
        }
        if(this.editTearsheetData.investmentProfile?.TS_IP_QBS){
            this.editTearsheetData.investmentProfile.TS_IP_QBS.isEdit = iseditable;
        }
        if(this.editTearsheetData.keyOperatingStats){
            for(let stats in this.editTearsheetData.keyOperatingStats){
                this.editTearsheetData.keyOperatingStats[stats]['isEdit'] = iseditable;
            }
        }

    }
  refreshTearsheetValues(statsArr){
      this.editTearsheetData.keyOperatingStats = statsArr ;
      this.getFieldNamesForNullStats(this.editTearsheetData.keyOperatingStats);
      this.getFieldNamesForNullStats(this.editTearsheetData.investmentProfile);
      this.onEditClick(this.isEditMode)
  }
  private getFieldNamesForNullStats(tearsheetSection:any): void {
        const metrics = tearsheetSection;
        for (const metric in metrics) {
            if (metrics[metric] === null || metrics[metric] === undefined) {
                this.subscription.add(this.metricCodesService.getMetricCode(metric)
                    .subscribe(metricCode => {
                      if (metricCode) {
                        metrics[metric] = {
                          displayName: metricCode.displayName,
                          tearsheetNameStyles: metricCode.tearsheetNameStyles,
                          tearsheetValueStyles: metricCode.tearsheetValueStyles,
                          isEdit: false,
                          unit: metricCode.unit,
                          value: null
                        };
                      }
                    }));
            }
        }
    }


}
